import React, { useContext, useEffect, useRef, useState } from "react"
import { Button, Form } from "react-bootstrap"
import log from "loglevel"
import JSONPretty from "react-json-prettify"
import { github } from "react-json-prettify/dist/themes"
import { JSONEditor } from "@json-editor/json-editor"
import UsuarioContext from "../Context/Usuario"
import Loading from "./Loading"
import Select from "react-select"
import UAI from "../UAI"
import { FiExternalLink } from "react-icons/fi"

/**
 * Componente que muestra un atributo, para ser editado, ya sea en un modal o en línea
 * @param {atributo, defaultValue, onChange} props
 */
const AtributoInput = (props) => {
  const { usuario } = useContext(UsuarioContext)
  const [jsonEditor, setJsonEditor] = useState(null)
  const [valido, setValido] = useState(null)
  const [loadingUrl, setLoadingUrl] = useState(false)
  const [valoresSelector, setValoresSelector] = useState(null)
  const [errorLoading, setErrorLoading] = useState(false)

  const ref = useRef()
  useEffect(() => {
    JSONEditor.defaults.languages.es = {
      error_notset: "Propiedad requerida",
      error_notempty: "Valor requerido",
      error_enum: "Valor debe ser uno de la lista",
      error_const: "Valor debe ser un valor constante",
      error_anyOf: "Valor debe validarse al menos con un esquema",
      error_oneOf:
        "Valor debe ser válido para al menos un esquema. Por ahora se valida con {{0}} esquemas.",
      error_not: "Valor no debe ser validarse según esquema",
      error_type_union: "Valor debe ser de uno de los tipos provistos",
      error_type: "Valor debe ser de tipo {{0}}",
      error_disallow_union: "Valor no debe ser de un tipo prohibído",
      error_disallow: "Valor no debe ser de tipo {{0}}",
      error_multipleOf: "Valor debe ser múltiplo de {{0}}",
      error_maximum_excl: "Valor debe ser menor a {{0}}",
      error_maximum_incl: "Valor debe ser a lo más {{0}}",
      error_minimum_excl: "Valor debe ser mayor que {{0}}",
      error_minimum_incl: "Valor debe ser al menos {{0}}",
      error_maxLength: "Valor debe tener a lo más {{0}} caracteres de largo",
      error_minLength: "Valor debe tener al menos {{0}} caracteres de largo",
      error_pattern: "Valor debe calzar con el patrón {{0}}",
      error_additionalItems: "No se permiten más objetos en el arreglo",
      error_maxItems: "Valor debe tener a lo más {{0}} ítemes",
      error_minItems: "Valor debe tener al menos {{0}} ítemes",
      error_uniqueItems: "La lista no permite repetir valores",
      error_maxProperties: "Objeto debe tener a lo más {{0}} propiedades",
      error_minProperties: "Objeto debe tener al menos {{0}} propiedades",
      error_required: "A objeto le falta la propiedad '{{0}}'",
      error_additional_properties:
        "No se permiten más propiedades, sin embargo propiedad {{0}} está configurada",
      error_property_names_exceeds_maxlength:
        "Nombre de la propiedad {{0}} excede largo máximo",
      error_property_names_enum_mismatch:
        "Nombre de la propiedad {{0}} no calza con valores permitidos",
      error_property_names_const_mismatch:
        "Property name {{0}} does not match the const value",
      error_property_names_pattern_mismatch:
        "Property name {{0}} does not match pattern",
      error_property_names_false:
        "Property name {{0}} fails when propertyName is false",
      error_property_names_maxlength:
        "Property name {{0}} cannot match invalid maxLength",
      error_property_names_enum:
        "Property name {{0}} cannot match invalid enum",
      error_property_names_pattern:
        "Property name {{0}} cannot match invalid pattern",
      error_property_names_unsupported: "Unsupported propertyName {{0}}",
      error_dependency: "Must have property {{0}}",
      error_date: "Date must be in the format {{0}}",
      error_time: "Time must be in the format {{0}}",
      error_datetime_local: "Datetime must be in the format {{0}}",
      error_invalid_epoch: "Date must be greater than 1 January 1970",
      error_ipv4:
        "Value must be a valid IPv4 address in the form of 4 numbers between 0 and 255, separated by dots",
      error_ipv6: "Value must be a valid IPv6 address",
      error_hostname: "The hostname has the wrong format",
      button_save: "Guardar",
      button_copy: "Copiar",
      button_cancel: "Cancelar",
      button_add: "Agregar",
      button_delete_all: "Todos",
      button_delete_all_title: "Borrar todo",
      button_delete_last: "Última/o {{0}}",
      button_delete_last_title: "Borrar última/o {{0}}",
      button_add_row_title: "Agregar {{0}}",
      button_move_down_title: "Mover abajo",
      button_move_up_title: "Mover arriba",
      button_properties: "Propiedades",
      button_object_properties: "Propiedades de Objeto",
      button_copy_row_title: "Copiar {{0}}",
      button_delete_row_title: "Borrar {{0}}",
      button_delete_row_title_short: "Borrar",
      button_copy_row_title_short: "Copiar",
      button_collapse: "Colapsar",
      button_expand: "Expandir",
      button_edit_json: "Editar JSON",
      button_upload: "Subir",
      flatpickr_toggle_button: "Cambiar",
      flatpickr_clear_button: "Limpiar",
      choices_placeholder_text: "Comience a escribir para agregar un valor",
      default_array_item_title: "ítem",
      button_delete_node_warning: "¿Segura/o que desea borrar?",
    }
    JSONEditor.defaults.language = "es"
    // Si el atributo es tipo JSON se utiliza JSONEditor para desplegar.
    if (
      ref &&
      ref.current &&
      props.atributo.Tipo === "Json" &&
      props.atributo.Schema
    ) {
      let _rawvalue = props.cohorte
        ? props.cohorte[props.atributo.key]
        : undefined
      try {
        _rawvalue = JSON.parse(_rawvalue)
        if (_rawvalue === null) {
          _rawvalue = undefined
        }
      } catch (e) {
        log.debug("Error parseando " + props.atributo.key)
        log.debug(_rawvalue)
        _rawvalue = undefined
      }
      if (!jsonEditor) {
        let options = {
          schema: props.atributo.Schema,
          theme: "bootstrap4",
          disable_collapse: true,
          compact: true,
          iconlib: "fontawesome4",
        }
        if (_rawvalue) {
          options.startval = _rawvalue
        }
        let editor = new JSONEditor(ref.current, options)

        editor.on("ready", () => {
          // Si no hay valor inicial desde el cohorte, usamos el de json-editor
          if (!_rawvalue) {
            _rawvalue = editor.getValue()
            // log.debug("Valor inicial " + props.atributo.key + ":" + _rawvalue)
          }
        })
        editor.on("change", () => {
          // Si no tenemos un valor original
          if (!_rawvalue) {
            log.debug("No tenemos valor por defecto")
            return
          }
          // Verificamos que no sea el valor por defecto y por ende cambie de null a este valor
          if (JSON.stringify(editor.getValue()) === JSON.stringify(_rawvalue)) {
            /*             log.debug(
              "No hay cambios en valores " +
                JSON.stringify(editor.getValue()) +
                " - " +
                JSON.stringify(_rawvalue)
            )
 */ return
          }
          log.debug(
            "Cambiamos! " +
              props.atributo.key +
              ":" +
              JSON.stringify(editor.getValue()) +
              " - " +
              JSON.stringify(_rawvalue)
          )
          // Invocamos el cambio de valor
          let event = { target: { value: editor.getValue() } }
          if (props.onChange) {
            props.onChange(event)
          }
        })
        setJsonEditor(editor)
      }
    }
    if (
      (props.atributo.Tipo === "Selector" ||
        props.atributo.Tipo === "SelectorMultiple") &&
      props.atributo.Url
    ) {
      setLoadingUrl(true)
      UAI.obtieneValoresAtributo(
        props.atributo,
        props.cohorte,
        usuario,
        (result) => {
          result = result.reduce((prev, current) => {
            prev.push({value:current.Id, label:current.Valor})
            return prev
          }, [])
          setValoresSelector(result)
          setLoadingUrl(false)
        },
        (error) => {
          log.debug(error)
          setErrorLoading(true)
        }
      )
    }
  }, [props.atributo])

  const obtieneAtributoInputType = (atributo) => {
    if (atributo.Tipo === "Numerico") {
      return "number"
    } else if (atributo.Tipo === "TextoGrande" || atributo.Tipo === "Html") {
      return "textarea"
    } else if (atributo.Tipo === "Fecha") {
      return "date"
    } else if (atributo.Tipo === "Selector") {
      return "select"
    } else if (atributo.Tipo === "Texto") {
      return "text"
    } else if (atributo.Tipo === "Booleano") {
      return "checkbox"
    } else if (atributo.Tipo === "Json") {
      return "json"
    } else if (atributo.Tipo === "SelectorMultiple") {
      return "typeahead"
    } else if (atributo.Tipo === "Archivo") {
      return "file"
    } else {
      return undefined
    }
  }

  const handleBlur = (e) => {
    log.debug("Blur!" + props.atributo.key)
    if (
      props.atributo.validar &&
      props.cohorte &&
      !props.atributo.validar(props.cohorte)
    ) {
      setValido(false)
    }
  }

  let type = obtieneAtributoInputType(props.atributo)

  if (!type) {
    return <div>Atributo inválido</div>
  }

  if (errorLoading) {
    return <div>Error cargando valores de atributo {props.atributo.nombre}</div>
  }

  if (props.atributo.input) {
    return props.atributo.input(props.cohorte)
  }

  let _rawvalue = props.cohorte ? props.cohorte[props.atributo.key] : undefined
  if (props.atributo.Tipo === "Fecha" && _rawvalue) {
    _rawvalue = _rawvalue.replace(/T.*$/, "")
  }
  if (_rawvalue === null) {
    _rawvalue = undefined
  }
  if (type === "typeahead") {
    let _newvalue = []
    if (_rawvalue) {
      try {
        let parsed = JSON.parse(_rawvalue)
        if (Array.isArray(parsed)) {
          _newvalue = parsed.reduce((p, c) => {
            p.push({"value":c.id,"label":c.text})
            return p
          }, [])
        }
      } catch (e) {
        log.error(e)
      }
    }
    _rawvalue = _newvalue
  }

  let _size = props.inline ? "md" : "md"

  if (
    type === "file"
  ) {
    return (
      <div>
        <Button
        variant="secondary"
          className="noprint"
          onClick={() => {
            const blob = UAI.b64toBlob(
              _rawvalue,
              "application/pdf"
            )
            const blobUrl = URL.createObjectURL(blob)
            window.open(blobUrl)
          }}
        >
          <FiExternalLink /> Abrir
        </Button>
      </div>
    )
  } else if (
    type === "select" &&
    props.atributo.ValoresAtributo
  ) {
    return (
      <Form.Select
        name={props.atributo.key}
        id={props.atributo.key}
        defaultValue={_rawvalue}
        size={_size}
        htmlSize="100%"
        multiple={type === "typeahead"}
        onChange={props.onChange}
        disabled={!props.atributo.editable || props.disabled}
        onBlur={handleBlur}
      >
        <option value="">Seleccione</option>
        {props.atributo.ValoresAtributo.map((valor) => {
          return (
            <option key={valor.Id} value={valor.Id}>
              {valor.Valor}
            </option>
          )
        })}
      </Form.Select>
    )
  } else if ((type === "select" || type === "typeahead") && loadingUrl) {
    return <Loading small />
  } else if ((type === "select" || type === "typeahead") && valoresSelector) {
    if(type==="typeahead") {
      return (
        <Select
          name={props.atributo.key}
          id={props.atributo.key}
          defaultValue={_rawvalue}
          size={_size}
          isMulti
          htmlSize="100%"
          onChange={props.onChange}
          disabled={!props.atributo.editable || props.disabled}
          onBlur={handleBlur}
          options={valoresSelector}
        />
      )  
    } else {
      return (
        <Form.Select
          name={props.atributo.key}
          id={props.atributo.key}
          defaultValue={_rawvalue}
          size={_size}
          htmlSize="100%"
          onChange={props.onChange}
          disabled={!props.atributo.editable || props.disabled}
          onBlur={handleBlur}
        >
          <option value="">Seleccione</option>
          {valoresSelector.map((valor) => {
            return (
              <option key={valor.value} value={valor.value}>
                {valor.label}
              </option>
            )
          })}
        </Form.Select>
      )  
    }
  } else if (type === "checkbox") {
    return (
      <>
        <Form.Select
          name={props.atributo.key}
          id={props.atributo.key}
          htmlSize="100%"
          defaultValue={_rawvalue ? "1" : "0"}
          onChange={props.onChange}
          size={_size}
          disabled={!props.atributo.editable || props.disabled}
          onBlur={handleBlur}
        >
          <option value="">Seleccione</option>
          <option value="0">No</option>
          <option value="1">Si</option>
        </Form.Select>
      </>
    )
  } else if (
    type === "textarea" ||
    (type === "text" && _rawvalue && _rawvalue.length > 30)
  ) {
    return (
      <Form.Control
        as="textarea"
        rows={props.inline ? 3 : 10}
        name={props.atributo.key}
        htmlSize="100%"
        id={props.atributo.key}
        size={_size}
        defaultValue={_rawvalue}
        onChange={props.onChange}
        disabled={!props.atributo.editable || props.disabled}
        onBlur={handleBlur}
      />
    )
  } else if (type === "json") {
    let jsonObject = undefined
    try {
      jsonObject = JSON.parse(_rawvalue)
    } catch (e) {
      jsonObject = undefined
    }
    if (props.atributo.Schema) {
      return <div id={"json-editor-" + props.atributo.key} ref={ref}></div>
    } else {
      return (
        <div id={"json-editor-" + props.atributo.key}>
          {jsonObject && (
            <JSONPretty json={JSON.parse(_rawvalue)} theme={github} />
          )}
          {!jsonObject && (
            <Form.Control
              as="textarea"
              rows={10}
              name={props.atributo.key}
              htmlSize="100%"
              id={props.atributo.key}
              size={_size}
              value={_rawvalue}
              onChange={props.onChange}
              disabled={
                (!props.atributo.editable || props.disabled) &&
                !usuario.esadministrador
              }
              onBlur={handleBlur}
            />
          )}
        </div>
      )
    }
  } else {
    return (
      <Form.Control
        type={type ? type : "text"}
        name={props.atributo.key}
        htmlSize="100%"
        id={props.atributo.key}
        defaultValue={_rawvalue}
        size={_size}
        onChange={props.onChange}
        disabled={!props.atributo.editable || props.disabled || !type}
        onBlur={handleBlur}
      />
    )
  }
}

export default AtributoInput
